import React, { useState, useMemo, useEffect, useCallback, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, parseISO, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Pencil, Trash2, CalendarDays, Link, Check, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { cn } from "../../lib/utils";
import { getPublicFileUrl } from "../../config";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import {
  getTable,
  getStatuses,
  updateItem,
  deleteItem,
} from "../../services/tarefas";

const TZ_BR = "America/Sao_Paulo";

const formatBrDate = (iso, withYear = true) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    const fmt = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      ...(withYear ? { year: "numeric" } : {}),
      timeZone: TZ_BR,
    });
    return fmt;
  } catch {
    return String(iso);
  }
};

const formatBrDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: TZ_BR,
    });
  } catch {
    return String(iso);
  }
};

const formatBrTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(String(iso));
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: TZ_BR,
    });
  } catch {
    return String(iso);
  }
};

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(203, 213, 225, ${alpha})`;
  const h = hex.replace("#", "");
  if (h.length !== 6) return `rgba(203, 213, 225, ${alpha})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
};

const initialsOf = (name) => {
  const s = (name || "").trim().split(/\s+/).filter(Boolean);
  if (s.length === 0) return "?";
  if (s.length === 1) return s[0].slice(0, 2).toUpperCase();
  return (s[0][0] + s[s.length - 1][0]).toUpperCase();
};

const simpleHash = (s) => {
  let h = 2166136261;
  const str = String(s || "?");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0);
};

const colorForName = (s) => {
  const palette = [
    "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
    "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
    "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef",
    "#ec4899", "#f43f5e",
  ];
  return palette[simpleHash(s) % palette.length];
};

const identiconSvgUrl = (name) => {
  const n = String(name || "?").trim() || "?";
  const bg = colorForName(n);
  const text = initialsOf(n);
  const w = 80;
  const h = 80;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<rect width="${w}" height="${h}" fill="${bg}"/>` +
    `<text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" ` +
    `fill="#ffffff" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" ` +
    `font-weight="700" font-size="30">${text}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const avatarSrcOf = (u) => {
  if (!u) return undefined;
  const raw = u.profilePicUrl || u.avatarUrl || u.avatar || u.picUrl || undefined;
  if (!raw) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  if (typeof raw !== "string") return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  const resolved = getPublicFileUrl(raw) || raw;
  if (!resolved) return identiconSvgUrl(u?.name || `user-${u?.id || "?"}`);
  return resolved;
};

const capitalizeTitle = (s) => {
  if (!s) return "";
  const t = String(s);
  if (t.length <= 1) return t.toUpperCase();
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

const deduplicateStatuses = (arr) => {
  const byName = new Map();
  const list = Array.isArray(arr) ? arr : [];
  for (const s of list) {
    const key = String(s.name || "Sem status").trim();
    if (!key) continue;
    if (!byName.has(key)) byName.set(key, s);
  }
  return Array.from(byName.values()).sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)
  );
};

const isStatusConcluido = (st) => {
  if (!st) return false;
  const n = String(st.name || "").trim().toLowerCase();
  return n === "feito" || n === "concluído" || n === "concluido" || n === "aprovado" || n === "concluido(a)";
};

const StatusBadgeInline = ({ item, statuses, onChange, saving }) => {
  const current = item.status;
  const currentId = current?.id ? String(current.id) : "";
  const cor = current?.color || "#94a3b8";

  return (
    <Select
      value={currentId}
      onValueChange={async (v) => {
        const novoId = v ? Number(v) : null;
        const novoStatus = statuses.find((s) => String(s.id) === v);
        await onChange(item.id, { statusId: novoId, status: novoStatus });
      }}
      disabled={saving === item.id}
    >
      <SelectTrigger
        className="h-8 min-w-[130px] max-w-[160px] border-none shadow-sm px-2.5 text-white text-xs font-semibold focus:ring-2 focus:ring-offset-1"
        style={{
          backgroundColor: cor,
          color: "#fff",
        }}
      >
        <SelectValue placeholder="Sem status">
          {current?.name || "Sem status"}
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        {statuses.map((st) => (
          <SelectItem key={st.id} value={String(st.id)}>
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full border border-black/10"
                style={{ backgroundColor: st.color }}
              />
              <span>{st.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

function TableView({ onAddItem, onEditItem, onRefresh, refreshKey = 0 }) {
  const { user: authUser } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  const buscar = useCallback(async () => {
    try {
      setCarregando(true);
      const [tbl, sts] = await Promise.all([getTable(), getStatuses()]);
      setGroups(Array.isArray(tbl?.groups) ? tbl.groups : []);
      setItems(Array.isArray(tbl?.items) ? tbl.items : []);
      setStatuses(deduplicateStatuses(sts));
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar tabela");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    buscar();
  }, [buscar, refreshKey]);

  const handleChangeStatusInline = async (id, payload) => {
    const statusId = payload.statusId;
    const statusObj = payload.status || null;
    const original = items.find((it) => String(it.id) === String(id));
    if (!original) return;

    const optimistic = items.map((it) =>
      String(it.id) === String(id)
        ? { ...it, status: statusObj || it.status }
        : it
    );
    setItems(optimistic);
    setSavingId(id);
    try {
      const updated = await updateItem(id, { statusId });
      setItems((prev) =>
        prev.map((it) =>
          String(it.id) === String(id) ? { ...it, ...updated } : it
        )
      );
      toast.success("Status atualizado");
      onRefresh?.();
    } catch (err) {
      console.error(err);
      setItems(
        items.map((it) =>
          String(it.id) === String(id) ? { ...it, status: original.status } : it
        )
      );
      toast.error("Erro ao atualizar status");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir tarefa "${item.title}"?`)) return;
    try {
      await deleteItem(item.id);
      setItems((prev) => prev.filter((it) => String(it.id) !== String(item.id)));
      toast.success("Tarefa excluída");
      onRefresh?.();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir tarefa");
    }
  };

  const handleCopiarLink = async (item) => {
    const url = `${window.location.origin}/tarefas/item/${encodeURIComponent(item.id)}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedLinkId(item.id);
      toast.success("Link da tarefa copiado");
      setTimeout(() => {
        setCopiedLinkId((prev) => (prev === item.id ? null : prev));
      }, 2000);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível copiar o link");
    }
  };

  const itemsByLane = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => m.set(g.id, []));
    items.forEach((it) => {
      const lid = it.laneId;
      if (!m.has(lid)) m.set(lid, []);
      m.get(lid).push(it);
    });
    return m;
  }, [groups, items]);

  const primeiraLane = groups[0];

  if (carregando) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground p-10">
        Carregando tabela...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6 pt-2">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Visão geral das tarefas da equipe
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tarefas organizadas por período, compatível com Monday.
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => primeiraLane && onAddItem?.(primeiraLane)}
          disabled={!primeiraLane}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova tarefa
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-md text-center p-8 border-2 border-dashed border-border rounded-lg">
            <h3 className="text-base font-semibold mb-1">Nenhum grupo ainda</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Mude para a aba Kanban para criar sua primeira coluna (grupo).
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6 overflow-y-auto pb-4 pr-1">
          {groups.map((lane) => {
            const itensLane = itemsByLane.get(lane.id) || [];
            const laneColor = lane.color || "#94a3b8";
            const laneNameDisplay = capitalizeTitle(lane.name);

            return (
              <div key={lane.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <h3
                    className="text-lg font-semibold tracking-tight"
                    style={{ color: laneColor }}
                  >
                    {laneNameDisplay}
                  </h3>
                  <span className="text-xs font-medium text-muted-foreground rounded-full px-2 py-0.5 bg-muted">
                    {itensLane.length}
                  </span>
                </div>

                <div
                  className="rounded-xl border border-border overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:shadow-none bg-card"
                >
                  <Table>
                    <TableHeader style={{ backgroundColor: hexToRgba(laneColor, 0.05) }}>
                      <TableRow className="border-border/60 hover:bg-transparent">
                        <TableHead className="w-[32%] pl-6">Tarefa</TableHead>
                        <TableHead className="w-[13%]">Resp.</TableHead>
                        <TableHead className="w-[14%]">Status</TableHead>
                        <TableHead className="w-[14%]">Concluída em</TableHead>
                        <TableHead className="w-[17%]">Progresso</TableHead>
                        <TableHead className="w-[10%] text-right pr-5">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itensLane.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={6}
                            className="p-6 text-center text-muted-foreground border-0"
                          >
                            Nenhuma tarefa em {laneNameDisplay}.
                          </TableCell>
                        </TableRow>
                      ) : (
                        itensLane.map((it) => {
                          // effectiveAssignee: prioridade assignee > dono (userId) > authUser logado
                          // Fallback authUser resolve casos de tarefas antigas com userId NULL na base
                          const ownerCandidate = it.user && it.user.id ? it.user : (it.userId ? { id: it.userId, name: "Responsável" } : null);
                          const effectiveAssignee = (it.assignee && it.assignee.id)
                            ? it.assignee
                            : (ownerCandidate
                                ? ownerCandidate
                                : (authUser?.id ? authUser : null));
                          const a = effectiveAssignee;
                          const effectiveAssigneeId = a ? String(a.id) : "";
                          const dueStr = String(it.dueDate || "").slice(0, 10);
                          const dueDate = dueStr ? parseISO(dueStr) : null;
                          const vencida =
                            dueDate && isBefore(dueDate, startOfDay(new Date()));
                          const progresso = Number(it.progressPct) || 0;
                          const concluida = isStatusConcluido(it.status);
                          const barCor =
                            vencida && !concluida ? "#dc2626" : "#10b981";
                          const trackCor =
                            vencida && !concluida
                              ? "rgba(220, 38, 38, 0.14)"
                              : "rgba(16, 185, 129, 0.16)";
                          const sharesExtra = Array.isArray(it.shares)
                            ? it.shares.filter((s) => {
                                if (!s?.user?.id) return false;
                                const sid = String(s.user.id);
                                return sid !== effectiveAssigneeId;
                              })
                            : [];
                          return (
                            <TableRow
                              key={it.id}
                              className="border-border/40 hover:bg-muted/40 dark:hover:bg-muted/20"
                              style={{
                                borderLeft: `3px solid ${laneColor}`,
                              }}
                            >
                              <TableCell className="pl-6 pr-3 align-top">
                                <div className="flex flex-col gap-0.5">
                                  <div
                                    className="text-sm font-semibold text-foreground cursor-pointer hover:underline"
                                    onClick={() => onEditItem?.(it)}
                                  >
                                    {it.title}
                                  </div>
                                  {it.description && (
                                    <div className="text-xs text-muted-foreground line-clamp-1">
                                      {it.description}
                                    </div>
                                  )}
                                  {Array.isArray(it.tags) && it.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {it.tags.map((t) => (
                                        <Badge
                                          key={t.id}
                                          variant="outline"
                                          className="text-[10px] py-0 px-1.5"
                                          style={{
                                            borderColor: hexToRgba(t.color, 0.35),
                                            color: t.color,
                                            backgroundColor: hexToRgba(t.color, 0.06),
                                          }}
                                        >
                                          {t.name}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="pr-3 align-top">
                                <div className="flex items-center gap-2">
                                  <div className="flex -space-x-2">
                                    {a && (
                                      <Avatar className="h-8 w-8 border-2 border-background z-10" title={a?.name}>
                                        <AvatarImage
                                          src={avatarSrcOf(a)}
                                          alt={a?.name || "?"}
                                        />
                                        <AvatarFallback className="text-[11px] font-semibold bg-muted text-muted-foreground">
                                          {initialsOf(a?.name)}
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                    {sharesExtra.slice(0, 4).map((s, i) => (
                                      <Avatar key={`share-${s.id || s.user.id}-${i}`} className="h-7 w-7 border-2 border-background" title={s.user.name}>
                                        <AvatarImage
                                          src={avatarSrcOf(s.user)}
                                          alt={s.user.name || "?"}
                                        />
                                        <AvatarFallback className="text-[10px] font-semibold bg-indigo-500/10 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                                          {initialsOf(s.user.name)}
                                        </AvatarFallback>
                                      </Avatar>
                                    ))}
                                    {sharesExtra.length > 4 && (
                                      <Avatar className="h-7 w-7 border-2 border-background bg-muted">
                                        <AvatarFallback className="text-[10px] font-semibold text-muted-foreground">
                                          +{sharesExtra.length - 4}
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                    {!a && sharesExtra.length === 0 && (
                                      <Avatar className="h-8 w-8 border-2 border-background">
                                        <AvatarFallback className="text-[11px] font-semibold bg-muted text-muted-foreground">
                                          ?
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                  </div>
                                  <div className="flex flex-col leading-tight">
                                    {a && (
                                      <span className="text-xs text-foreground/80 truncate max-w-[100px]">
                                        {a.name}
                                      </span>
                                    )}
                                    {sharesExtra.length > 0 && (
                                      <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">
                                        +{sharesExtra.length} compartilhado{sharesExtra.length === 1 ? "" : "s"}
                                      </span>
                                    )}
                                    {!a && sharesExtra.length === 0 && (
                                      <span className="text-xs text-muted-foreground italic">
                                        Não atribuído
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="pr-3 align-top">
                                <StatusBadgeInline
                                  item={it}
                                  statuses={statuses}
                                  onChange={handleChangeStatusInline}
                                  saving={savingId}
                                />
                              </TableCell>
                              <TableCell className="pr-3 align-top">
                                {it.completedAt ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400"
                                    title={`Concluída em ${formatBrDateTime(it.completedAt)}`}
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                                    {formatBrDate(it.completedAt, false)}
                                  </span>
                                ) : (
                                  <span className="inline-flex text-[11px] text-muted-foreground/70 italic">
                                    —
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="pr-3 align-top">
                                <div className="flex flex-col gap-1.5">
                                  <Progress
                                    value={progresso}
                                    overrideBarColor={barCor}
                                    overrideTrackColor={trackCor}
                                    className="h-2"
                                  />
                                  <div className="flex items-center justify-between">
                                    {dueDate ? (
                                      <span
                                        className={cn(
                                          "flex items-center gap-1 text-[11px]",
                                          vencida && !concluida
                                            ? "text-red-600 dark:text-red-400 font-semibold"
                                            : "text-muted-foreground"
                                        )}
                                      >
                                        <CalendarDays className="h-3 w-3" />
                                        {format(dueDate, "dd/MMM", { locale: ptBR })}
                                      </span>
                                    ) : (
                                      <span className="text-[11px] text-muted-foreground/70 italic">
                                        Sem data
                                      </span>
                                    )}
                                    <span className="text-[11px] font-medium text-foreground/70 tabular-nums">
                                      {progresso}%
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="pr-5 align-top">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400"
                                    onClick={() => handleCopiarLink(it)}
                                    aria-label="Copiar link da tarefa"
                                    title="Copiar link da tarefa"
                                  >
                                    {copiedLinkId === it.id ? (
                                      <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                    ) : (
                                      <Link className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                    onClick={() => onEditItem?.(it)}
                                    aria-label="Editar"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                                    onClick={() => handleDelete(it)}
                                    aria-label="Excluir"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TableView;
